import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Warning,
  ErrorOutline,
  Schedule,
  ReportProblem,
  FactCheck,
  HealthAndSafety,
} from "@mui/icons-material";
import {
  useGetSheqDashboardQuery,
  SheqIncident,
  SheqPpeRecord,
} from "../../../redux/sheqApi";

const NAVY = "#123456";

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  color?: string;
}) {
  return (
    <Card sx={{ flex: "1 1 150px", minWidth: 130 }}>
      <CardContent
        sx={{ display: "flex", alignItems: "center", gap: 2, p: 2.5 }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: `${color ?? NAVY}18`,
            color: color ?? NAVY,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {label}
          </Typography>
          <Typography variant="h5" fontWeight={700} color={color}>
            {value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

const SEVERITY_COLOR: Record<string, string> = {
  LOW: "#2e7d32",
  MEDIUM: "#f57c00",
  HIGH: "#c62828",
  CRITICAL: "#4a148c",
};

const TYPE_LABEL: Record<string, string> = {
  NEAR_MISS: "Near Miss",
  FIRST_AID: "First Aid",
  MEDICAL_TREATMENT: "Medical Treatment",
  LOST_TIME: "Lost Time",
  FATALITY: "Fatality",
  PROPERTY_DAMAGE: "Property Damage",
  ENVIRONMENTAL: "Environmental",
  UNSAFE_ACT: "Unsafe Act",
  UNSAFE_CONDITION: "Unsafe Condition",
};

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${dateStr}T00:00:00`);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

interface Props {
  blockId?: string;
}

export const SheqDashboard: React.FC<Props> = ({ blockId }) => {
  const { data, isLoading } = useGetSheqDashboardQuery({ blockId });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  const ppe = data?.ppe;
  const incidents = data?.incidents;
  const inspections = data?.inspections;

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {new Date().toLocaleDateString("en-GB", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
        })}
      </Typography>

      {/* ── PPE stats ── */}
      <Typography variant="h6" fontWeight={600} color={NAVY} mb={1.5}>
        PPE Status
      </Typography>
      <Box display="flex" gap={2} flexWrap="wrap" mb={4}>
        <StatCard
          icon={<CheckCircle sx={{ fontSize: 20 }} />}
          label="Active issues"
          value={ppe?.active ?? "—"}
          color="#2e7d32"
        />
        <StatCard
          icon={<Schedule sx={{ fontSize: 20 }} />}
          label="Expiring ≤ 30 days"
          value={ppe?.expiringSoon ?? "—"}
          color={(ppe?.expiringSoon ?? 0) > 0 ? "#f57c00" : "#2e7d32"}
        />
        <StatCard
          icon={<ErrorOutline sx={{ fontSize: 20 }} />}
          label="Expired"
          value={ppe?.expired ?? "—"}
          color={(ppe?.expired ?? 0) > 0 ? "#c62828" : "#555"}
        />
        <StatCard
          icon={<Warning sx={{ fontSize: 20 }} />}
          label="Damaged / Lost"
          value={(ppe?.damaged ?? 0) + (ppe?.lost ?? 0)}
          color={
            (ppe?.damaged ?? 0) + (ppe?.lost ?? 0) > 0 ? "#e65100" : "#555"
          }
        />
        <StatCard
          icon={<ReportProblem sx={{ fontSize: 20 }} />}
          label="Open incidents"
          value={incidents?.open ?? "—"}
          color={(incidents?.open ?? 0) > 0 ? "#c62828" : "#2e7d32"}
        />
        <StatCard
          icon={<FactCheck sx={{ fontSize: 20 }} />}
          label="Inspections (all)"
          value={inspections?.total ?? "—"}
        />
        <StatCard
          icon={<HealthAndSafety sx={{ fontSize: 20 }} />}
          label="Inspections failed"
          value={inspections?.failed ?? "—"}
          color={(inspections?.failed ?? 0) > 0 ? "#c62828" : "#555"}
        />
      </Box>

      <Box display="flex" gap={3} flexWrap="wrap">
        {/* Expiring soon list */}
        {(data?.expiringRecords?.length ?? 0) > 0 && (
          <Card sx={{ flex: "1 1 320px" }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color={NAVY}
                mb={1}
              >
                Expiring Soon (next 30 days)
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {data!.expiringRecords.map((r: SheqPpeRecord) => {
                const days = daysUntil(r.replacementDueDate);
                return (
                  <Box
                    key={r.id}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={0.75}
                    borderBottom="1px solid #f0f0f0"
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {r.workerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.ppeName}
                        {r.blockName ? ` · ${r.blockName}` : ""}
                      </Typography>
                    </Box>
                    <Chip
                      label={days <= 0 ? "Expired" : `${days}d`}
                      size="small"
                      sx={{
                        bgcolor:
                          days <= 0
                            ? "#c62828"
                            : days <= 7
                            ? "#e65100"
                            : "#f57c00",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    />
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Recent incidents */}
        {(data?.recentIncidents?.length ?? 0) > 0 && (
          <Card sx={{ flex: "1 1 320px" }}>
            <CardContent>
              <Typography
                variant="subtitle1"
                fontWeight={700}
                color={NAVY}
                mb={1}
              >
                Recent Incidents
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {data!.recentIncidents.map((inc: SheqIncident) => (
                <Box
                  key={inc.id}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  py={0.75}
                  borderBottom="1px solid #f0f0f0"
                >
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {inc.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {inc.incidentDate}
                      {inc.blockName ? ` · ${inc.blockName}` : ""}
                    </Typography>
                  </Box>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    gap={0.5}
                    ml={1}
                  >
                    <Chip
                      label={TYPE_LABEL[inc.type] ?? inc.type}
                      size="small"
                      sx={{ fontSize: 10, height: 18 }}
                    />
                    <Chip
                      label={inc.severity}
                      size="small"
                      sx={{
                        bgcolor: SEVERITY_COLOR[inc.severity] ?? "#555",
                        color: "#fff",
                        fontSize: 10,
                        height: 18,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}

        {/* All-clear state */}
        {(data?.expiringRecords?.length ?? 0) === 0 &&
          (data?.recentIncidents?.length ?? 0) === 0 && (
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={6}
              sx={{ color: "#2e7d32" }}
            >
              <CheckCircle sx={{ fontSize: 56, mb: 1 }} />
              <Typography variant="h6" fontWeight={600}>
                All clear
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No expiring PPE or open incidents.
              </Typography>
            </Box>
          )}
      </Box>
    </Box>
  );
};
